import React from 'react'
import { useLocation } from 'react-router-dom'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo'
import VisibilityIcon from '@mui/icons-material/Visibility'
import LoadingButton from '@mui/lab/LoadingButton'
import CheckIcon from '@mui/icons-material/Check'

import config from '../../config'
import utils from '../../utils/utils'

// Admin route (first path segment) → Next.js frontend path (list/index page).
// '' = index route which renders Strutture, so same as strutture (campeggi).
const ADMIN_TO_FRONTEND_PATH = {
  '': '/campeggi-e-villaggi/scopri-i-camping-village',
  strutture: '/campeggi-e-villaggi/scopri-i-camping-village',
  localita: '/localita',
  attrazioni: '/esperienze/booking-esperienze',
  eventi: '/eventi-e-festivita',
  news: '/news',
  offerte: '/offerte',
  servizi: '/campeggi-e-villaggi/scopri-i-camping-village',
  itinerari: '/pianifica-viaggio',
  tematiche: '/vacanze-tematiche',
  guide: '/guide-turistiche',
  partners: '/i-nostri-partner',
  'media-kit': '/media-kit',
  categorie: '/esperienze',
}

// Base path for entity detail pages (path + slug). Only sections with a Next.js [slug] page.
// Itinerari are experiences (tipologia 2) and use the same page as attrazioni/servizi: /esperienze/[nome_esperienza].
const ADMIN_DETAIL_BASE_PATH = {
  '': '/campeggi-e-villaggi',
  strutture: '/campeggi-e-villaggi',
  localita: '/localita',
  attrazioni: '/esperienze',
  eventi: '/eventi-e-festivita',
  news: '/news',
  offerte: '/offerte',
  servizi: null,
  itinerari: '/esperienze',
  tematiche: '/vacanze-tematiche',
  guide: '/guide-turistiche',
  partners: '/i-nostri-partner',
}

// Section config for the elementi prop. Presence of key in array = section visible.
export const ELEMENTI_CONFIG = [
  { key: 'form', label: 'FORM', id: 1 },
  { key: 'contenuti', label: 'CONTENUTI', id: 2 },
  { key: 'stagionalita', label: "STAGIONALITA'", id: 3 },
  { key: 'localita', label: "LOCALITA'", id: 4 },
  { key: 'strutture', label: 'STRUTTURE', id: 5 },
  { key: 'date', label: 'DATE', id: 6 },
  { key: 'sistemazioni', label: 'SISTEMAZIONI', id: 7 },
  { key: 'servizi', label: 'SERVIZI', id: 8 },
  { key: 'esperienze', label: 'ESPERIENZE', id: 9 },
  { key: 'immagini', label: 'IMMAGINI', id: 10 },
  { key: 'allegati', label: 'ALLEGATI', id: 11 },
  { key: 'alloggi', label: 'ALLOGGI', id: 12 },
  { key: 'tematiche', label: 'TEMATICHE', id: 13 },
  { key: 'categorie', label: 'CATEGORIE', id: 14 },
  { key: 'attrazioni', label: 'ATTRAZIONI', id: 15 },
  { key: 'itinerari', label: 'ITINERARI', id: 16 },
  { key: 'guide', label: 'GUIDE', id: 17 },
  { key: 'eventi', label: 'EVENTI', id: 18 },
  { key: 'news', label: 'NEWS', id: 19 },
  { key: 'seo', label: 'SEO', id: 20 },
  { key: 'faq', label: 'FAQ', id: 21 },
]

function HeaderScheda({
  titolo,
  nome,
  situazione,
  cambiaSituazione,
  elementi,
  dataCreazione,
  dataModifica,
  schedaCopiata,
  doPopolaSchedaCopiata,
  doIncollaSchedaCopiata,
  copiaAbilitato,
  isModifica,
  doElimina,
  modifiche,
  loading,
  conferma,
  annulla,
  viewPageSlug,
  viewPageSection,
  viewPageDisabled = false,
  viewPageDisabledReason,
  viewPageHidden = false,
}) {
  const location = useLocation()
  const segments = location.pathname.split('/').filter(Boolean)
  const sectionFromRoute = segments[0] ?? ''
  const section = viewPageSection != null ? viewPageSection : sectionFromRoute
  const listPath = ADMIN_TO_FRONTEND_PATH[section]
  const detailBase = ADMIN_DETAIL_BASE_PATH[section]
  const path =
    viewPageSlug && detailBase != null
      ? `${detailBase}/${viewPageSlug}`
      : listPath != null
        ? listPath.startsWith('/')
          ? listPath
          : `/${listPath}`
        : null
  const viewPageUrl = !viewPageHidden && path != null ? `${config.sitoUrl.replace(/\/$/, '')}${path}` : null
  const has = (key) => Array.isArray(elementi) && elementi.includes(key)

  return (
    <div className={isModifica ? 'admin-container-lungo' : 'admin-container'}>
      <div className="admin-container-prima-riga">
        <h3 className="admin-titoletto">
          {titolo}
          {nome ? ': ' + nome : ''}
        </h3>
        <Stack direction="row" spacing={3}>
          {viewPageUrl && (
            <Tooltip
              placement="top"
              PopperProps={{
                modifiers: [{ name: 'offset', options: { offset: [0, -8] } }],
                sx: { '& .MuiTooltip-tooltip': { maxWidth: 'none', whiteSpace: 'nowrap' } },
              }}
              title={viewPageDisabled ? viewPageDisabledReason || 'Pagina non visibile sul sito' : viewPageUrl}
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={viewPageDisabled}
                  onClick={() => !viewPageDisabled && window.open(viewPageUrl, '_blank', 'noopener,noreferrer')}
                  startIcon={<VisibilityIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  VEDI PAGINA
                </Button>
              </span>
            </Tooltip>
          )}
          {!isModifica && schedaCopiata && (
            <Button
              size="small"
              variant={'contained'}
              color={'primary'}
              onClick={doIncollaSchedaCopiata}
              startIcon={<ContentPasteGoIcon />}
            >
              INCOLLA
            </Button>
          )}
          {isModifica && copiaAbilitato && (
            <>
              <Button
                size="small"
                variant={'contained'}
                color={'primary'}
                onClick={doPopolaSchedaCopiata}
                startIcon={<ContentCopyIcon />}
              >
                COPIA
              </Button>
              <Button
                size="small"
                variant={'contained'}
                color={'error'}
                onClick={doElimina}
                startIcon={<HighlightOffIcon />}
              >
                ELIMINA
              </Button>
            </>
          )}
        </Stack>
      </div>

      {(dataCreazione || dataModifica) && (
        <Stack direction="row" alignItems="center" spacing={7} paddingBottom={2}>
          {dataCreazione && (
            <span className="admin-date-header-bold">
              Creazione:{' '}
              <span className="admin-date-header">
                {utils.scriviData(new Date(dataCreazione))} alle {utils.scriviOra(new Date(dataCreazione))}
              </span>
            </span>
          )}
          {dataModifica && (
            <span className="admin-date-header-bold">
              Modifica:{' '}
              <span className="admin-date-header">
                {utils.scriviData(new Date(dataModifica))} alle {utils.scriviOra(new Date(dataModifica))}
              </span>
            </span>
          )}
        </Stack>
      )}

      <div className="admin-container-seconda-riga">
        <Stack direction="row" gap={2} flexWrap="wrap">
          {ELEMENTI_CONFIG.filter((el) => has(el.key)).map((el) => (
            <Button
              key={el.key}
              size="small"
              variant="outlined"
              color={situazione === el.id ? 'primary' : 'chiaro'}
              onClick={() => cambiaSituazione(el.id)}
            >
              {el.label}
            </Button>
          ))}
        </Stack>
      </div>

      {modifiche && (
        <Stack direction="row" spacing={5} paddingTop={3}>
          <LoadingButton
            loading={loading}
            loadingPosition="start"
            size="small"
            variant={'contained'}
            color={'success'}
            onClick={conferma}
            type="submit"
            startIcon={<CheckIcon />}
          >
            CONFERMA
          </LoadingButton>
          <Button size="small" variant={'contained'} color={'error'} onClick={annulla} startIcon={<HighlightOffIcon />}>
            ANNULLA
          </Button>
        </Stack>
      )}
    </div>
  )
}

export default HeaderScheda
